/**
 * Event target sits inside an apartment / floor SVG link that should keep normal
 * click navigation (pointer capture on the zoom container otherwise steals the gesture).
 */
function isFloorPlanSvgNavLinkTarget(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) {
    return false;
  }
  const anchor = target.closest<HTMLAnchorElement>('.svg-wrap a[href]');
  if (!anchor) {
    return false;
  }
  const href = (anchor.getAttribute('href') ?? '').trim();
  if (href === '' || href === '#') {
    return false;
  }
  if (anchor.getAttribute('aria-disabled') === 'true') {
    return false;
  }
  return true;
}

const MOUSE_FOLLOW_TOOLTIP_OFFSET_X = 0;
const MOUSE_FOLLOW_TOOLTIP_OFFSET_Y = 0;
/** Past this fraction of viewport width, flip tooltip to the left of the cursor anchor. */
const MOUSE_FOLLOW_TOOLTIP_FLIP_X_RATIO = 0.5;

/**
 * Position a fixed mouse-follow tooltip: default to the right of the cursor; in the right
 * portion of the viewport, translate so the box sits left of the anchor (avoids clipping).
 */
function positionMouseFollowTooltip(tooltip: HTMLElement, e: MouseEvent): void {
  const x = e.clientX + MOUSE_FOLLOW_TOOLTIP_OFFSET_X;
  const y = e.clientY - MOUSE_FOLLOW_TOOLTIP_OFFSET_Y;
  tooltip.style.left = `${x}px`;
  tooltip.style.top = `${y}px`;
  const flipX = e.clientX > window.innerWidth * MOUSE_FOLLOW_TOOLTIP_FLIP_X_RATIO;
  if (flipX) {
    tooltip.style.transformOrigin = '0 0';
    tooltip.style.transform = 'translate(-100%, -100%)';
  } else {
    tooltip.style.transformOrigin = '';
    tooltip.style.transform = 'translate(0, -100%)';
  }
}

/**
 * House-floor plan tooltip (mouse-follow) for links to floor terms.
 */
export function initFloorPlanTooltip(): void {
  initLegacyFloorTooltip();
  initApartmentFloorTooltip();
  initParkingSpotTooltip();
}

export function initFloorPlanZoom(): void {
  const controls = document.querySelector<HTMLElement>('[data-floor-plan-controls]');
  const target = document.querySelector<HTMLElement>('[data-floor-zoom-target]');
  if (!controls || !target) {
    return;
  }

  const zoomInBtn = controls.querySelector<HTMLButtonElement>('[data-floor-zoom-in]');
  const zoomOutBtn = controls.querySelector<HTMLButtonElement>('[data-floor-zoom-out]');
  if (!zoomInBtn || !zoomOutBtn) {
    return;
  }

  const minScale = 1;
  const maxScale = 5;
  const step = target.dataset.floorZoomStep === '1' ? 1 : 0.5;
  let scale = 1;
  let translateX = 0;
  let translateY = 0;
  let activePointerId: number | null = null;
  let startX = 0;
  let startY = 0;
  let startTranslateX = 0;
  let startTranslateY = 0;
  let didPan = false;
  let suppressClick = false;

  const clamp = (value: number, min: number, max: number): number => {
    return Math.min(max, Math.max(min, value));
  };

  const clampPan = (x: number, y: number): { x: number; y: number } => {
    const viewport = target.parentElement as HTMLElement | null;
    if (!viewport) {
      return { x, y };
    }

    const maxPanX = Math.max(0, ((scale - 1) * viewport.clientWidth) / 2);
    const maxPanY = Math.max(0, ((scale - 1) * viewport.clientHeight) / 2);
    return {
      x: clamp(x, -maxPanX, maxPanX),
      y: clamp(y, -maxPanY, maxPanY),
    };
  };

  const render = (): void => {
    if (scale <= minScale) {
      translateX = 0;
      translateY = 0;
    } else {
      const clamped = clampPan(translateX, translateY);
      translateX = clamped.x;
      translateY = clamped.y;
    }

    target.style.transform = `translate(${translateX}px, ${translateY}px) scale(${scale.toFixed(2)})`;
    target.style.touchAction = scale > minScale ? 'none' : 'auto';
    target.classList.toggle('is-pannable', scale > minScale);
    zoomOutBtn.disabled = scale <= minScale;
    zoomInBtn.disabled = scale >= maxScale;
  };

  zoomInBtn.addEventListener('click', () => {
    scale = Math.min(maxScale, Number((scale + step).toFixed(2)));
    render();
  });

  zoomOutBtn.addEventListener('click', () => {
    scale = Math.max(minScale, Number((scale - step).toFixed(2)));
    render();
  });

  target.addEventListener('dragstart', (e: DragEvent) => {
    if (scale > minScale) {
      e.preventDefault();
    }
  });

  target.addEventListener('pointerdown', (e: PointerEvent) => {
    if (scale <= minScale) {
      return;
    }
    if (e.pointerType !== 'touch' && e.button !== 0) {
      return;
    }
    // Let real <a> hits receive clicks: capture + is-panning would break navigation.
    if (isFloorPlanSvgNavLinkTarget(e.target)) {
      return;
    }

    e.preventDefault();

    activePointerId = e.pointerId;
    startX = e.clientX;
    startY = e.clientY;
    startTranslateX = translateX;
    startTranslateY = translateY;
    didPan = false;
  });

  target.addEventListener('pointermove', (e: PointerEvent) => {
    if (activePointerId !== e.pointerId) {
      return;
    }

    const dx = e.clientX - startX;
    const dy = e.clientY - startY;
    if (!didPan && Math.hypot(dx, dy) > 4) {
      didPan = true;
      try {
        target.setPointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
      target.classList.add('is-panning');
      document.body.classList.add('floor-plan-is-panning');
    }
    if (!didPan) {
      return;
    }

    const clamped = clampPan(startTranslateX + dx, startTranslateY + dy);
    translateX = clamped.x;
    translateY = clamped.y;
    e.preventDefault();
    render();
  });

  const endPan = (e: PointerEvent): void => {
    if (activePointerId !== e.pointerId) {
      return;
    }

    if (didPan) {
      suppressClick = true;
      window.setTimeout(() => {
        suppressClick = false;
      }, 0);
    }

    activePointerId = null;
    didPan = false;
    target.classList.remove('is-panning');
    document.body.classList.remove('floor-plan-is-panning');
    if (target.hasPointerCapture(e.pointerId)) {
      target.releasePointerCapture(e.pointerId);
    }
  };

  target.addEventListener('pointerup', endPan);
  target.addEventListener('pointercancel', endPan);

  target.addEventListener(
    'click',
    (e: MouseEvent) => {
      if (!suppressClick) {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      suppressClick = false;
    },
    true
  );

  render();
}

function initLegacyFloorTooltip(): void {
  const tooltip = document.getElementById('floor-plan-tooltip');
  if (!tooltip) {
    return;
  }

  const links = document.querySelectorAll<HTMLAnchorElement>(
    '.svg-wrap a[data-floor-tooltip="1"]'
  );
  if (links.length < 1) {
    return;
  }

  const houseEl = tooltip.querySelector<HTMLElement>('[data-floor-tooltip-house]');
  const floorEl = tooltip.querySelector<HTMLElement>('[data-floor-tooltip-floor]');
  const freeEl = tooltip.querySelector<HTMLElement>('[data-floor-tooltip-free]');
  const reservedEl = tooltip.querySelector<HTMLElement>('[data-floor-tooltip-reserved]');
  if (!houseEl || !floorEl || !freeEl || !reservedEl) {
    return;
  }

  let moveHandler: ((e: MouseEvent) => void) | null = null;

  const updatePos = (e: MouseEvent): void => {
    positionMouseFollowTooltip(tooltip, e);
  };

  links.forEach((link) => {
    link.addEventListener('mouseenter', (e: MouseEvent) => {
      houseEl.textContent = (link.dataset.houseName ?? '').trim();
      floorEl.textContent = (link.dataset.title ?? '').trim();
      freeEl.textContent = String(parseInt(link.dataset.free ?? '0', 10) || 0);
      reservedEl.textContent = String(parseInt(link.dataset.reserved ?? '0', 10) || 0);

      tooltip.classList.add('is-visible');
      updatePos(e);

      moveHandler = updatePos;
      document.addEventListener('mousemove', moveHandler);
    });

    link.addEventListener('mouseleave', () => {
      tooltip.classList.remove('is-visible');
      if (moveHandler) {
        document.removeEventListener('mousemove', moveHandler);
        moveHandler = null;
      }
    });
  });
}

function initApartmentFloorTooltip(): void {
  const tooltip = document.getElementById('floor-apartment-tooltip');
  if (!tooltip) {
    return;
  }

  const links = document.querySelectorAll<HTMLAnchorElement>(
    '.svg-wrap a[data-apartment-tooltip="1"]'
  );
  if (links.length < 1) {
    return;
  }

  const houseEl = tooltip.querySelector<HTMLElement>('[data-floor-apartment-house]');
  // const floorEl = tooltip.querySelector<HTMLElement>('[data-floor-apartment-floor]');
  const titleEl = tooltip.querySelector<HTMLElement>('[data-floor-apartment-title]');
  const areaEl = tooltip.querySelector<HTMLElement>('[data-floor-apartment-area]');
  const roomsEl = tooltip.querySelector<HTMLElement>('[data-floor-apartment-rooms]');
  const statusEl = tooltip.querySelector<HTMLElement>('[data-floor-apartment-status]');
  const statusDotEl = tooltip.querySelector<HTMLElement>('[data-floor-apartment-status-dot]');
  if (!houseEl || !roomsEl || !titleEl || !areaEl || !statusEl || !statusDotEl) {
    return;
  }

  let moveHandler: ((e: MouseEvent) => void) | null = null;

  const updatePos = (e: MouseEvent): void => {
    positionMouseFollowTooltip(tooltip, e);
  };

  links.forEach((link) => {
    link.addEventListener('mouseenter', (e: MouseEvent) => {
      let statusClass = (
        link.dataset.apartmentStatus ??
        link.dataset.status ??
        ''
      )
        .trim()
        .toLowerCase();
      if (statusClass === '') {
        if (link.classList.contains('laisvas')) {
          statusClass = 'laisvas';
        } else if (link.classList.contains('rezervuotas')) {
          statusClass = 'rezervuotas';
        } else if (link.classList.contains('parduotas')) {
          statusClass = 'parduotas';
        }
      }

      houseEl.textContent = (link.dataset.houseName ?? '').trim();
      roomsEl.textContent = (link.dataset.apartmentRooms ?? '1').trim() + ' k.';
      titleEl.textContent = (link.dataset.apartmentTitle ?? '').trim();
      areaEl.textContent = (link.dataset.apartmentArea ?? '').trim();
      statusEl.textContent = (link.dataset.apartmentStatusLabel ?? '').trim();

      statusDotEl.classList.remove(
        'floor-apartment-tooltip__status-dot--free',
        'floor-apartment-tooltip__status-dot--reserved',
        'floor-apartment-tooltip__status-dot--sold'
      );
      if (statusClass === 'laisvas') {
        statusDotEl.classList.add('floor-apartment-tooltip__status-dot--free');
      } else if (statusClass === 'rezervuotas') {
        statusDotEl.classList.add('floor-apartment-tooltip__status-dot--reserved');
      } else if (statusClass === 'parduotas') {
        statusDotEl.classList.add('floor-apartment-tooltip__status-dot--sold');
      }

      tooltip.classList.add('is-visible');
      updatePos(e);

      moveHandler = updatePos;
      document.addEventListener('mousemove', moveHandler);
    });

    link.addEventListener('mouseleave', () => {
      tooltip.classList.remove('is-visible');
      if (moveHandler) {
        document.removeEventListener('mousemove', moveHandler);
        moveHandler = null;
      }
    });
  });
}

function initParkingSpotTooltip(): void {
  const tooltip = document.getElementById('parking-spot-tooltip');
  // Parking zoom uses [data-parking-plan] while zoom is disabled; restore [data-floor-zoom-target] when re-enabled.
  const wrap = document.querySelector<HTMLElement>('[data-parking-plan] .svg-wrap, [data-floor-zoom-target] .svg-wrap');
  if (!tooltip || !wrap) {
    return;
  }

  const idEl = tooltip.querySelector<HTMLElement>('[data-parking-tooltip-id]');
  const statusEl = tooltip.querySelector<HTMLElement>('[data-parking-tooltip-status]');
  const statusDotEl = tooltip.querySelector<HTMLElement>('[data-parking-tooltip-status-dot]');
  const areaEl = tooltip.querySelector<HTMLElement>('[data-parking-tooltip-area]');
  const areaRowEl = tooltip.querySelector<HTMLElement>('[data-parking-tooltip-area-row]');
  if (!idEl || !statusEl || !statusDotEl || !areaEl || !areaRowEl) {
    return;
  }

  let activeGroup: SVGGElement | null = null;
  let moveHandler: ((e: MouseEvent) => void) | null = null;

  const updatePos = (e: MouseEvent): void => {
    positionMouseFollowTooltip(tooltip, e);
  };

  const clearMove = (): void => {
    if (moveHandler) {
      document.removeEventListener('mousemove', moveHandler);
      moveHandler = null;
    }
  };

  const applySpot = (g: SVGGElement, e: MouseEvent): void => {
    clearMove();

    let statusClass = (g.dataset.status ?? '').trim().toLowerCase();
    if (statusClass === '') {
      if (g.classList.contains('laisvas')) {
        statusClass = 'laisvas';
      } else if (g.classList.contains('parduotas')) {
        statusClass = 'parduotas';
      }
    }

    idEl.textContent = (g.dataset.parkingId ?? '').trim();
    statusEl.textContent = (g.dataset.statusLabel ?? '').trim();
    const area = (g.dataset.parkingArea ?? '').trim();
    areaEl.textContent = area === '' ? '' : `Plotas: ${area} kv. m`;
    areaRowEl.hidden = area === '';

    statusDotEl.classList.remove(
      'floor-apartment-tooltip__status-dot--free',
      'floor-apartment-tooltip__status-dot--reserved',
      'floor-apartment-tooltip__status-dot--sold'
    );
    if (statusClass === 'laisvas') {
      statusDotEl.classList.add('floor-apartment-tooltip__status-dot--free');
    } else if (statusClass === 'rezervuotas') {
      statusDotEl.classList.add('floor-apartment-tooltip__status-dot--reserved');
    } else if (statusClass === 'parduotas') {
      statusDotEl.classList.add('floor-apartment-tooltip__status-dot--sold');
    }

    tooltip.classList.add('is-visible');
    tooltip.setAttribute('aria-hidden', 'false');
    updatePos(e);
    moveHandler = updatePos;
    document.addEventListener('mousemove', moveHandler);
  };

  wrap.addEventListener('mouseover', (e: MouseEvent) => {
    const t = e.target;
    if (!(t instanceof Element)) {
      return;
    }
    const g = t.closest<SVGGElement>('g.parking[data-parking-tooltip="1"]');
    if (!g) {
      return;
    }
    if (activeGroup === g) {
      return;
    }
    activeGroup = g;
    applySpot(g, e);
  });

  wrap.addEventListener('mouseout', (e: MouseEvent) => {
    const related = e.relatedTarget;
    if (activeGroup && related instanceof Node && activeGroup.contains(related)) {
      return;
    }
    tooltip.classList.remove('is-visible');
    tooltip.setAttribute('aria-hidden', 'true');
    clearMove();
    activeGroup = null;
  });
}
